import _ from "lodash";
import React, { useEffect, useRef, useState, useContext } from "react";
import MonthNavigator from "./monthNavigator/MonthNavigator";
import SummaryAmountTag from "./monthSummary/SummaryAmountTag";
import TransactionListMobile from "./transactions/TransactionListMobile";
import Api from "../../utils/Api";
import CardLayout from "./common/CardLayout";
import SimulationPanel from "../common/simulation/SimulationPanel";
import ExpenseTypeSummary from "./expenseTypeSummary/ExpenseTypeSummary";

import BlockUi from "react-block-ui";
import "react-block-ui/style.css";
import { Loader } from "react-loaders";
import "loaders.css/loaders.min.css";
import ExpenseCategoryDetails from "./expenseCategoryDetails/ExpenseCategoryDetails";
import IncomeCategoryDetails from "./incomeCategoryDetails/IncomeCategoryDetails";
import Insights from "../desktop/Insights/Insights";
import { findTransactionsByCategory } from "../../utils/TransactionFinder";
import { summaryCalculation } from "../Util";
import { TransactionType } from "../../utils/constants";

const CURRENT_VIEW = {
    EXPENSE: TransactionType.EXPENSE,
    INCOME: TransactionType.INCOME,
};
import { AppContext } from "../../AppContext";

export default function MobileMain() {
    const [lookups, setLookups] = useState(null);

    const [currentView, setCurrentView] = useState(CURRENT_VIEW.EXPENSE);

    const [monthTransactions, setMonthTransactions] = useState(null);

    const [showCategoryTransactions, setShowCategoryTransactions] = useState(false);
    const [selectedCategoryTransactions, setSelectedCategoryTransactions] = useState([]);
    const [selectedCategoryDescription, setSelectedCategoryDescription] = useState("");
    const [originalTransactions, setOriginalTransactions] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    const filterInstance = useRef(null);
    const categoryTransactionsRef = useRef(null);
    const summaryAmountTagInstance = useRef(null);
    const expenseTypeSummaryRef = useRef(null);
    const expenseCategoryDetailsRef = useRef(null);
    const incomeCategoryDetailsRef = useRef(null);
    const insightsInstance = useRef(null);
    const categoryTransactionContainerRef = useRef(null);

    const {
        ignoredCategories,
        ignoredTags,
        selectedYear,
        selectedMonthId,
        setSelectedMonthId,
        setSelectedYear,
        simulatedTransactions,
        getTransactionSimulation,
    } = useContext(AppContext);

    useEffect(async () => {
        setShowCategoryTransactions(false);

        await loadData();

        if (currentView === CURRENT_VIEW.EXPENSE || !currentView) {
            expenseClickedCallback();
        } else {
            incomeClickedCallback();
        }
    }, [ignoredTags, ignoredCategories, selectedYear, selectedMonthId]);

    useEffect(() => {
        const relevantSimulatedTransactions = getTransactionSimulation(
            selectedYear,
            selectedMonthId,
        );

        if (!relevantSimulatedTransactions || !Object.keys(relevantSimulatedTransactions).length) {
            const {
                balance: originalBalance,
                expense: originalExpense,
                income: originalIncome
            } = summaryAmountTagInstance.current.getOriginalItems();

            summaryAmountTagInstance.current.setItems({
                balance: originalBalance,
                expense: originalExpense,
                income: originalIncome
            });

            return;
        }

        console.log(`********* simulatedTransactions changed: ${relevantSimulatedTransactions}`);

        const {
            balance: originalBalance,
            expense: originalExpense,
            income: originalIncome
        } = summaryAmountTagInstance.current.getOriginalItems();

        const { newBalance, newIncome, newExpense } = summaryCalculation(
            originalBalance,
            originalIncome,
            originalExpense,
            Object.values(relevantSimulatedTransactions)
        );

        summaryAmountTagInstance.current.setItems({
            balance: newBalance,
            expense: newExpense,
            income: newIncome
        });
    }, [simulatedTransactions]);

    async function loadData() {
        setIsLoading(true);
        const { response: lookups } = await Api.lookups();
        setLookups(lookups);

        filterInstance.current.setLookups(lookups);

        const {
            income: originalIncome,
            expense: originalExpense,
            balance: originalBalance
        } = await getBalanceSummary();

        const relevantSimulatedTransactions = getTransactionSimulation(
            selectedYear,
            selectedMonthId,
        );

        const {
            newBalance: balance,
            newIncome: income,
            newExpense: expense
        } = summaryCalculation(
            originalBalance,
            originalIncome,
            originalExpense,
            Object.values(relevantSimulatedTransactions)
        );

        summaryAmountTagInstance.current.setOriginalItems({
            balance: originalBalance,
            income: originalIncome,
            expense: originalExpense
        });
        summaryAmountTagInstance.current.setItems({ balance, expense, income });

        const {
            currentExpenseCategories,
            variableExpenseCategories,
            incomeCategories,
            variableExpensesSum,
            currentExpensesSum,
            currentExpensesDiffInPercentage,
            variableExpensesDiffInPercentage,
            insights,
            simulationRecommendation
        } = await getCategorySummary();

        const currentCategorySummary = _.sortBy(currentExpenseCategories, "sortOrder");
        const variantCategorySummary = shrinkForNumberOfCategories(variableExpenseCategories, 4);
        const parsedIncomeCategories = shrinkForNumberOfCategories(incomeCategories, 4);
        incomeCategoryDetailsRef.current.setItems(parsedIncomeCategories);
        expenseCategoryDetailsRef.current.setItems({
            currentCategorySummary,
            variantCategorySummary
        });

        expenseTypeSummaryRef.current.setItems({
            variableExpensesSum,
            currentExpensesSum,
            currentExpensesDiffInPercentage,
            variableExpensesDiffInPercentage
        });

        const { response: transactions } = await Api.getMonthTransactions({
            year: selectedYear,
            month: selectedMonthId,
            ignoreCategories: [...ignoredCategories],
            ignoreTags: [...ignoredTags]
        });
        setOriginalTransactions(transactions);

        setMonthTransactions({
            expenseTransactions: transactions.expenseTransactions.reverse(),
            incomeTransactions: transactions.incomeTransactions
        });

        if (insightsInstance) {
            insights.forEach((insight) => {
                insight.transactions = findTransactionsByCategory(
                    transactions.expenseTransactions,
                    transactions.incomeTransactions,
                    insight.category.categoryId
                );
            });
            insightsInstance.current.setInsights(insights);
        }


        if (filterInstance) {
            filterInstance.current.setSimulationRecommendation(
                simulationRecommendation,
                selectedMonthId,
                selectedYear,
            );
        }
        setIsLoading(false);
    }

    function shrinkForNumberOfCategories(variableExpenseCategories, numberOfCategories) {
        const categories = variableExpenseCategories.slice(0, numberOfCategories);
        const otherCategories = variableExpenseCategories.slice(numberOfCategories, numberOfCategories.length);
        categories.push({
            amount: _.chain(otherCategories).sumBy("amount").round(2),
            category: "אחר",
            categoryId: -1
        });
        return categories;
    }

    async function getCategorySummary() {
        const params = {
            year: selectedYear,
            month: selectedMonthId,
            ignoreCategories: ignoredCategories,
            ignoreTags: ignoredTags
        };

        const { response: items } = await Api.getMonthCategories(params);
        return items;
    }

    async function getBalanceSummary() {
        const params = {
            year: selectedYear,
            month: selectedMonthId,
            ignoreCategories: ignoredCategories,
            ignoreTags: ignoredTags
        };
        const { response: items } = await Api.expenseVsIncomeByTime(params);
        return items[0] ? items[0] : { income: 0, expense: 0, balance: 0 };
    }

    const incomeClickedCallback = () => {
        setCurrentView(CURRENT_VIEW.INCOME);

        incomeCategoryDetailsRef.current.show();
        expenseTypeSummaryRef.current.hide();
        expenseCategoryDetailsRef.current.hide();

        setShowCategoryTransactions(false);
    };

    const expenseClickedCallback = () => {
        setCurrentView(CURRENT_VIEW.EXPENSE);

        expenseTypeSummaryRef.current.show();
        expenseCategoryDetailsRef.current.show();
        incomeCategoryDetailsRef.current.hide();

        setShowCategoryTransactions(false);
    };

    function getTouches(evt) {
        return (
            evt.touches || // browser API
            evt.originalEvent.touches
        ); // jQuery
    }

    function handleTouchStart(evt) {
        const firstTouch = getTouches(evt)[0];
        xDown = firstTouch.clientX;
        yDown = firstTouch.clientY;
    }

    let xDown = null;
    let yDown = null;

    function handleTouchMove(evt) {
        if (!xDown || !yDown) {
            return;
        }

        let xUp = evt.touches[0].clientX;
        let yUp = evt.touches[0].clientY;

        let xDiff = xDown - xUp;
        let yDiff = yDown - yUp;

        const targetIsInInsightItem = isTargetOrChildOf(evt.touches[0].target, "insightsContainer");

        const touchEventTriggeredFromRangePicker = evt.touches[0].target.className === "form-range";
        if (!touchEventTriggeredFromRangePicker && Math.abs(xDiff) > Math.abs(yDiff) && !targetIsInInsightItem) {
            /*most significant*/
            let newMonthId = null;
            if (xDiff > 0) {
                if (Number(selectedMonthId) - 1 < 1) {
                    //previous year
                    newMonthId = 12;
                    console.log(`selectedYear: ${Number(selectedYear) - 1}`);
                    setSelectedYear(Number(selectedYear) - 1);
                } else {
                    newMonthId = Number(selectedMonthId) - 1;
                }
                console.log(`newMonthId: ${newMonthId}`);
                setSelectedMonthId(newMonthId);
            } else {
                if (Number(selectedMonthId) + 1 > 12) {
                    //next year
                    newMonthId = 1;
                    console.log(`selectedYear: ${Number(selectedYear) + 1}`);
                    setSelectedYear(Number(selectedYear) + 1);
                } else {
                    newMonthId = Number(selectedMonthId) + 1;
                }
                console.log(`newMonthId: ${newMonthId}`);
                setSelectedMonthId(newMonthId);
            }
        }
        /* reset values */
        xDown = null;
        yDown = null;
    }

    function isTargetOrChildOf(target, id) {
        if (target.id === id) {
            return true;
        }
        if (target.parentElement) {
            return isTargetOrChildOf(target.parentElement, id);
        }
        return false;
    }

    function currentCategoryWasClicked(categoryId, categoryDescription) {
        setSelectedCategoryTransactions(
            originalTransactions.expenseTransactions.filter((item) => item.categoryId === categoryId)
        );
        setSelectedCategoryDescription(categoryDescription);
        setShowCategoryTransactions(true);
        categoryTransactionContainerRef.current.scrollIntoView();
    }

    function variantCategoryWasClicked(categoryId, categoryDescription) {
        setSelectedCategoryTransactions(
            originalTransactions.expenseTransactions.filter((item) => item.categoryId === categoryId)
        );
        setSelectedCategoryDescription(categoryDescription);
        setShowCategoryTransactions(true);
        categoryTransactionContainerRef.current.scrollIntoView();
    }

    function incomeCategoryWasClicked(categoryId, categoryDescription) {
        setSelectedCategoryTransactions(
            originalTransactions.incomeTransactions.filter((item) => item.categoryId === categoryId)
        );
        setSelectedCategoryDescription(categoryDescription);
        setShowCategoryTransactions(true);
        categoryTransactionsRef.current.scrollIntoView();
    }

    return (
        <BlockUi
            tag="div"
            blocking={isLoading}
            loader={<Loader active type={"ball-pulse"} color="#02a17c" />}
            message={"...טוען נתונים"}
        >
            <div>
                <div
                    style={{
                        position: "fixed",
                        height: "270px",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 900,
                        background: "#e6e6e6"
                    }}
                >
                    <MonthNavigator />

                    <div
                        style={{
                            position: "absolute",
                            right: "20%",
                            bottom: "59%"
                        }}
                    >
                        <SimulationPanel
                            iconWidth={"28px"}
                            selectedTags={ignoredTags}
                            selectedCategories={ignoredCategories}
                            ref={filterInstance}
                            isMobile={true}
                        />
                    </div>

                    <SummaryAmountTag
                        incomeClicked={currentView === CURRENT_VIEW.INCOME}
                        expenseClicked={currentView === CURRENT_VIEW.EXPENSE}
                        incomeClickedCallback={incomeClickedCallback}
                        expenseClickedCallback={expenseClickedCallback}
                        ref={summaryAmountTagInstance}
                    />
                </div>
                <div
                    style={{ overflow: "auto", marginTop: "270px" }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                >
                    <ExpenseTypeSummary ref={expenseTypeSummaryRef} />

                    <ExpenseCategoryDetails
                        currentCategoryWasClicked={currentCategoryWasClicked}
                        variantCategoryWasClicked={variantCategoryWasClicked}
                        ref={expenseCategoryDetailsRef}
                    ></ExpenseCategoryDetails>

                    <IncomeCategoryDetails
                        incomeCategoryWasClicked={incomeCategoryWasClicked}
                        ref={incomeCategoryDetailsRef}
                    ></IncomeCategoryDetails>

                    <div ref={categoryTransactionContainerRef}>
                        {showCategoryTransactions && selectedCategoryTransactions.length && (
                            <CardLayout header={`פירוט עסקאות לקטגורית ${selectedCategoryDescription}`}>
                                <div ref={categoryTransactionsRef}>
                                    <TransactionListMobile
                                        height={"260px"}
                                        lookups={lookups}
                                        transactions={selectedCategoryTransactions}
                                        transactionType={currentView}
                                    />
                                </div>
                            </CardLayout>
                        )}
                    </div>

                    {showCategoryTransactions === false &&
                        currentView === CURRENT_VIEW.EXPENSE &&
                        monthTransactions?.expenseTransactions && (
                            <CardLayout header={"הוצאות בסדר יורד"}>
                                <TransactionListMobile
                                    height={"260px"}
                                    lookups={lookups}
                                    transactions={monthTransactions.expenseTransactions}
                                    transactionType={currentView}
                                />
                            </CardLayout>
                        )}

                    {showCategoryTransactions === false &&
                        currentView === CURRENT_VIEW.INCOME &&
                        monthTransactions?.incomeTransactions && (
                            <CardLayout header={"הכנסות בסדר יורד"}>
                                <TransactionListMobile
                                    height={"260px"}
                                    lookups={lookups}
                                    transactions={monthTransactions.incomeTransactions}
                                    transactionType={currentView}
                                />
                            </CardLayout>
                        )}
                    <div style={{ marginBottom: "150px" }}>
                        <CardLayout header={"תובנות"} showCardStyle={false}>
                            <Insights ref={insightsInstance} isMobile={true} />
                        </CardLayout>
                    </div>
                </div>
            </div>
        </BlockUi>
    );
}
